import { useEffect, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { LuArrowLeft } from 'react-icons/lu'
import { Button } from '@cannect/new-components/atoms'
import { SUBSCRIPTIONS_PAGE_PATH } from '@cannect/services/resources/recurrency/constants'
import { useResumeSubscription, useSubscription } from '@cannect/services/resources/recurrency/hooks'
import { getBlockedReason, getRecurrencyError } from '@cannect/services/resources/recurrency/helpers'
import type { SubscriptionDocumentType } from '@cannect/services/resources/recurrency/requests'
import type { SubscriptionItem } from '@cannect/services/resources/recurrency/types'
import { cn } from '@cannect/lib/utils'
import { subscriptionsCopy } from '../copy'
import { useDetailDeepLink } from '../hooks/useSubscriptionDeepLink'
import { useSubscriptionAction } from '../hooks/useSubscriptionAction'
import { LoadError } from '../List/LoadError'
import { AddItemDialog } from '../dialogs/AddItemDialog'
import { CancelDialog } from '../dialogs/CancelDialog'
import { ChangeCardDialog } from '../dialogs/ChangeCardDialog'
import { PauseDialog } from '../dialogs/PauseDialog'
import { PostponeDialog } from '../dialogs/PostponeDialog'
import { RemoveItemDialog } from '../dialogs/RemoveItemDialog'
import { SkipDialog } from '../dialogs/SkipDialog'
import { UploadDocumentDialog } from '../dialogs/UploadDocumentDialog'
import { ActionsBar, type ActionDialog } from './ActionsBar'
import { AlertBanners } from './AlertBanners'
import { CyclesTimeline } from './CyclesTimeline'
import { DeliveryPaymentSection } from './DeliveryPaymentSection'
import { canUploadDocument, documentNeedsAction, DocumentsSection } from './DocumentsSection'
import { DetailHeader } from './DetailHeader'
import { HistoryList } from './HistoryList'
import { ItemsSection } from './ItemsSection'
import { SubscriptionDetailSkeleton } from './SubscriptionDetailSkeleton'

type Dialog = null | ActionDialog | 'card' | 'add' | { remove: SubscriptionItem } | { upload: SubscriptionDocumentType }

const isObject = <K extends string>(dialog: Dialog, key: K): dialog is Extract<Dialog, Record<K, unknown>> =>
  typeof dialog === 'object' && dialog !== null && key in dialog

export const SubscriptionDetail = ({ id }: { id: number }) => {
  const history = useHistory()
  const { data: detail, isLoading, error, refetch } = useSubscription(id)
  const deepLink = useDetailDeepLink(id)
  const documentsRef = useRef<HTMLElement>(null)
  const cardButtonRef = useRef<HTMLButtonElement>(null)
  const [dialog, setDialog] = useState<Dialog>(null)
  const [panel, setPanel] = useState<string | null>(null)
  const [docReasons, setDocReasons] = useState<{ reasons: string[]; messages: string[] } | null>(null)
  const [uploaded, setUploaded] = useState<SubscriptionDocumentType[]>([])
  const [lastRemoveItem, setLastRemoveItem] = useState<SubscriptionItem | null>(null)

  const notFound = error && getRecurrencyError(error).status === 404

  useEffect(() => {
    if (!notFound) return
    toast.error(subscriptionsCopy.notFound)
    history.replace(SUBSCRIPTIONS_PAGE_PATH)
  }, [notFound, history])

  const goToDocuments = () => {
    const node = documentsRef.current
    if (!node) return
    node.scrollIntoView({ behavior: 'smooth', block: 'start' })
    node.focus({ preventScroll: true })
  }

  // Links dos e-mails: aplica uma vez depois do primeiro carregamento e limpa a URL.
  useEffect(() => {
    if (!detail || !deepLink.target) return
    const { target } = deepLink
    if (target.type === 'card') {
      setPanel('card')
      setDialog('card')
    } else {
      setPanel('documents')
      requestAnimationFrame(goToDocuments)
      if (canUploadDocument(detail, target.doc) && (detail.status === 'active' || detail.status === 'paused')) {
        setDialog({ upload: target.doc })
      }
    }
    deepLink.consume()
  }, [detail, deepLink.target])

  const resume = useSubscriptionAction({
    detail: detail || { id, cycleDays: 0 },
    mutation: useResumeSubscription(id),
    event: 'recurrency_resume',
    onErrorCode: {
      DOCUMENTS_NOT_VALID: (err) => {
        setDocReasons({ reasons: err.reasons, messages: err.messages })
        setPanel('documents')
        requestAnimationFrame(goToDocuments)
      }
    },
    onSuccess: () => setDocReasons(null)
  })

  if (isLoading) return <SubscriptionDetailSkeleton />
  if (notFound) return null
  if (error || !detail) {
    return <LoadError onRetry={() => refetch()} message={subscriptionsCopy.loadError.detail} />
  }

  const open = detail.status === 'active' || detail.status === 'paused'
  const cardBlocked = getBlockedReason(detail, 'card')
  const closeDialog = (next: boolean) => {
    if (!next) setDialog(null)
  }

  const cardButton = (tone: 'default' | 'alert') =>
    open ? (
      <Button
        ref={tone === 'default' ? cardButtonRef : undefined}
        size="md"
        variant={tone === 'alert' ? 'default' : 'outline_light'}
        className={cn(tone === 'alert' && 'bg-red-700 text-white hover:bg-red-700/90')}
        disabled={Boolean(cardBlocked)}
        title={cardBlocked || undefined}
        onClick={() => setDialog('card')}>
        {subscriptionsCopy.buttons.changeCard}
      </Button>
    ) : null

  const renderPrescriptionAction = () => {
    if (!open || detail.isDependent || !documentNeedsAction(detail, 'prescription')) return null
    if (!canUploadDocument(detail, 'prescription')) {
      return (
        <Button asChild size="md">
          <Link to="/agendamento">{subscriptionsCopy.documents.schedule}</Link>
        </Button>
      )
    }
    return (
      <Button size="md" onClick={() => setDialog({ upload: 'prescription' })}>
        {subscriptionsCopy.documents.sendPrescription}
      </Button>
    )
  }

  return (
    <div className="flex w-full flex-col gap-4" data-panel={panel || undefined}>
      <Link
        to={SUBSCRIPTIONS_PAGE_PATH}
        className="flex items-center gap-2 self-start text-sm font-semibold text-primary-700 no-underline">
        <LuArrowLeft aria-hidden />
        {subscriptionsCopy.back}
      </Link>

      <h1 className="text-2xl font-semibold text-neutral-800">
        {detail.isDependent && detail.personName ? subscriptionsCopy.dependent(detail.personName) : 'Minha assinatura'}
      </h1>

      <AlertBanners
        detail={detail}
        onGoToDocuments={goToDocuments}
        cardAction={cardButton('alert')}
        documentAction={renderPrescriptionAction()}
      />
      <DetailHeader detail={detail} />
      <ActionsBar
        detail={detail}
        onOpen={setDialog}
        onResume={() => resume.run(undefined as void)}
        resumePending={resume.isPending}
      />
      <ItemsSection
        detail={detail}
        onAddItem={() => setDialog('add')}
        onRemoveItem={(item) => {
          setLastRemoveItem(item)
          setDialog({ remove: item })
        }}
        onRequestPrescription={
          canUploadDocument(detail, 'prescription') ? () => setDialog({ upload: 'prescription' }) : goToDocuments
        }
      />
      <DeliveryPaymentSection detail={detail} cardAction={cardButton('default')} />
      <DocumentsSection
        ref={documentsRef}
        detail={detail}
        reasons={docReasons}
        uploaded={uploaded}
        onUpload={(type) => setDialog({ upload: type })}
      />
      <CyclesTimeline detail={detail} />
      <HistoryList detail={detail} />

      {open && (
        <>
          <SkipDialog detail={detail} open={dialog === 'skip'} onOpenChange={closeDialog} />
          <PostponeDialog detail={detail} open={dialog === 'postpone'} onOpenChange={closeDialog} />
          <PauseDialog detail={detail} open={dialog === 'pause'} onOpenChange={closeDialog} />
          <CancelDialog
            detail={detail}
            open={dialog === 'cancel'}
            onOpenChange={closeDialog}
            onSwitchTo={(next) => setDialog(next)}
          />
          <ChangeCardDialog detail={detail} open={dialog === 'card'} onOpenChange={closeDialog} />
          <AddItemDialog detail={detail} open={dialog === 'add'} onOpenChange={closeDialog} />
          <RemoveItemDialog
            detail={detail}
            item={isObject(dialog, 'remove') ? dialog.remove : lastRemoveItem}
            open={isObject(dialog, 'remove')}
            onOpenChange={closeDialog}
            onCancelSubscription={() => setDialog('cancel')}
          />
          <UploadDocumentDialog
            detail={detail}
            type={isObject(dialog, 'upload') ? dialog.upload : 'prescription'}
            open={isObject(dialog, 'upload')}
            onOpenChange={closeDialog}
            onUploaded={(type) => setUploaded((current) => Array.from(new Set([...current, type])))}
          />
        </>
      )}
    </div>
  )
}
